import { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import Button from "common/core/button";
import WorkflowModalV2 from "common/modals/workflow_modal/v2";
import ProofLogo from "assets/images/logos/proof-logo-full.svg";
import Icon from "common/core/icon";
import { Requirement } from "common/meeting/notary/requirements";
import ProgressBar from "common/core/progress_bar";
import Svg from "common/core/svg";
import {
  ProofMeetingOTPCodeForm,
  PROOF_OTP_TITLE_COPY,
} from "common/notary/certificate/proof/proof_otp";
import { useQuery } from "util/graphql";
import { DigicertSigningStatus, MeetingEndedState } from "graphql_globals";

import DocumentBundleHashingStatus from "./document_bundle_hashing_status_query.graphql";
import Styles from "./proof_otp_modal.module.scss";
import { useNotaryMeetingContext } from "./context";
import { constructUiReqs } from "./header/complete";
import type { NotaryCompleteMeeting as Meeting } from "./header/complete_fragment.graphql";

type ModalProps = {
  onClose: () => void;
  onCompleteMeeting: () => Promise<unknown>;
  onPostCompleteMeeting: (meetingId: string) => void;
  meeting: Meeting;
  numOfUnConfirmedInstructions: number;
};

type CertSigningState =
  | { type: "none" } // meeting hasn't been terminated
  | { type: "loading" } // actively refreshing hashStatus until confirmed or error
  | { type: "confirm" } // hashStatus is ready, user needs to submit their otp
  | { type: "signingStatusError" }; // hashStatus is an error

const MISSING_REQ_TITLE = (
  <FormattedMessage
    id="e2fed730-2b46-4c9f-aed1-1fc2088be663"
    defaultMessage="Hm, looks like you're missing requirements..."
  />
);

function getTitle({ type }: CertSigningState) {
  switch (type) {
    case "none":
      return (
        <div className={Styles.title}>
          <Icon name="success" className={Styles.successIcon} />
          <FormattedMessage
            id="a25b25e7-dabf-4d6b-a6dc-2b3dc087f9b2"
            defaultMessage="All meeting requirements fulfilled!"
          />
        </div>
      );
    case "loading":
      return (
        <FormattedMessage
          id="241546dd-e7d6-4098-8cc8-76e90468239b"
          defaultMessage="Processing documents..."
        />
      );
    case "confirm":
      return (
        <div>
          <Svg className={Styles.proofLogo} src={ProofLogo} alt="proof-logo" />
          <div>{PROOF_OTP_TITLE_COPY}</div>
        </div>
      );
    case "signingStatusError":
      return (
        <FormattedMessage
          id="9d82dc78-cae4-46a0-af3b-242508884362"
          defaultMessage="We’re currently unable to process these documents"
        />
      );
  }
}

export function ProofCompleteMeetingModal({
  meeting,
  onClose,
  onCompleteMeeting,
  onPostCompleteMeeting,
  numOfUnConfirmedInstructions,
}: ModalProps) {
  const { id: meetingId, unfulfilledRequirements } = meeting;
  const { analytics, simulatedNotaryProfile } = useNotaryMeetingContext();
  const [certStatus, setCertStatus] = useState<CertSigningState>({ type: "none" });

  const navigate = useNavigate();
  const { data, stopPolling, startPolling } = useQuery(DocumentBundleHashingStatus, {
    variables: { meetingId },
  });

  useEffect(() => {
    if (
      data?.meeting?.__typename !== "Meeting" ||
      certStatus.type === "none" ||
      data.meeting.endedState === MeetingEndedState.NOT_COMPLETED
    ) {
      return;
    }

    const { hashingStatus } = data.meeting.documentBundle!;
    if (hashingStatus === DigicertSigningStatus.PROCESSING) {
      return;
    } else if (hashingStatus === DigicertSigningStatus.READY) {
      setCertStatus({ type: "confirm" });
    } else {
      setCertStatus({ type: "signingStatusError" });
    }

    stopPolling();
  }, [data?.meeting]);

  const requirements = constructUiReqs(unfulfilledRequirements, numOfUnConfirmedInstructions);
  const missingReq = Boolean(requirements.length);

  const handleComplete = () => {
    return onCompleteMeeting().then(() => {
      if (!simulatedNotaryProfile) {
        analytics.onCompleteMeeting(missingReq);
      }
      setCertStatus({ type: "loading" });
      startPolling(1000);
    });
  };

  const getButtons = () => {
    switch (certStatus.type) {
      case "none":
        return [
          <Button
            key="1"
            variant="secondary"
            buttonColor="action"
            automationId="return-to-meeting"
            onClick={onClose}
          >
            <FormattedMessage
              id="4c62803c-ec56-4a93-a749-1d299e239963"
              defaultMessage="Return to meeting"
            />
          </Button>,
          <Button
            key="0"
            buttonColor={missingReq ? "danger" : "action"}
            variant="primary"
            automationId="complete-button"
            onClick={handleComplete}
            disabled={missingReq}
          >
            <FormattedMessage
              id="540c3142-b943-43b6-bfd4-5b03cbda5300"
              defaultMessage="Complete Meeting"
            />
          </Button>,
        ];

      case "signingStatusError":
        return [
          <Button
            key="0"
            buttonColor="action"
            variant="primary"
            onClick={() => {
              navigate("/");
            }}
          >
            <FormattedMessage
              id="aebf77bd-f491-410c-974d-cd34a297daff"
              defaultMessage="Return to dashboard"
            />
          </Button>,
        ];
      case "loading":
      case "confirm":
        return [];
    }
  };

  return (
    <WorkflowModalV2
      className="NotaryMeetingCompleteModal"
      title={missingReq ? MISSING_REQ_TITLE : getTitle(certStatus)}
      buttons={getButtons()}
      footerSeparator={false}
    >
      {certStatus.type === "none" ? (
        <>
          {!missingReq && (
            <div>
              <FormattedMessage
                id="06f1da1c-82e6-44ab-a601-e722784ca8fd"
                defaultMessage="Next, finalize the meeting by authenticating your certificate"
                tagName="p"
              />
            </div>
          )}
          <div>
            {requirements.map(({ key, value }) => (
              <Requirement key={key} value={value} messageKey={key} />
            ))}
          </div>
        </>
      ) : certStatus.type === "loading" ? (
        <>
          <FormattedMessage
            id="f9240452-98e5-4d20-adbc-2d4aee3f75b8"
            defaultMessage="Do not close this window."
            tagName="p"
          />
          <FormattedMessage
            id="9a1aece8-bc34-4e28-a615-9ab60a998558"
            defaultMessage="This should only take a few moments."
            tagName="p"
          />
          <div className={Styles.progressBar}>
            <ProgressBar barClassName={Styles.progressBarAnimation} />
          </div>
        </>
      ) : certStatus.type === "confirm" ? (
        <div>
          <FormattedMessage
            id="9a66f205-19c8-4f06-bcd8-cfcdbe680844"
            defaultMessage="Secure your signatures by entering the authenticator app code for your Proof Certificate. This step is crucial for the signer to receive their document"
          />
          <ProofMeetingOTPCodeForm meetingId={meetingId} onSuccess={onPostCompleteMeeting} />
        </div>
      ) : (
        <FormattedMessage
          id="80b6ae80-b0ec-44f0-84d4-e79c67a36e03"
          defaultMessage="Please try again later."
          tagName="p"
        />
      )}
    </WorkflowModalV2>
  );
}
